import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, usersAPI, refreshAccessToken } from '../services/api';
import * as webauthn from '../utils/webauthn';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      let storedToken = sessionStorage.getItem('token');
      const storedUser = sessionStorage.getItem('user');

      // KAN-531: this tab was signed in (the user record is here) but its access token is
      // gone. Ask the refresh cookie for a new one before treating the learner as signed
      // out. Only when the user record is present, so an anonymous visitor's page load
      // never calls /auth/refresh. A new tab still starts signed out, as before.
      if (!storedToken && storedUser) {
        try {
          storedToken = await refreshAccessToken();
        } catch (e) {
          storedToken = null;
        }
      }

      if (storedToken && storedUser) {
        try {
          const response = await usersAPI.getMe();
          // /users/me returns an envelope ({ user, profile, activity counts… });
          // the auth user is the inner record — same shape the login response
          // carries. Storing the envelope made role/name read undefined after a
          // refresh, so admins lost the admin link and /admin bounced them.
          const me = response.data && response.data.user ? response.data.user : response.data;
          setUser(me);
          try { sessionStorage.setItem('user', JSON.stringify(me)); } catch { /* noop */ }
          setToken(storedToken);
        } catch (error) {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      if (!storedToken && storedUser) {
        sessionStorage.removeItem('user');
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      const { access_token: newToken, user: userData } = response.data;

      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || String(d)).join('; ')
        : detail || error.response?.data?.message || 'Login failed. Please try again.';
      // KAN-443: the server refuses an unverified account with a 403 that tells the user to
      // request a new verification email. Flag it so the sign-in page can offer that.
      const needsEmailVerification =
        error.response?.status === 403 && /verify your email/i.test(String(errorMsg));
      return { success: false, error: errorMsg, needsEmailVerification };
    } finally {
      setLoading(false);
    }
  };

  // Passkey (WebAuthn) login — backend + helpers shipped with the security port.
  const loginWithPasskey = async (email) => {
    setLoading(true);
    try {
      const options = (await authAPI.webauthnLoginBegin(email)).data;
      const credential = await webauthn.getAssertion(options);
      const response = await authAPI.webauthnLoginComplete(email, credential);
      const { access_token: newToken, user: userData } = response.data;

      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      // NotAllowedError = user cancelled the browser passkey prompt.
      if (error?.name === 'NotAllowedError') {
        return { success: false, error: 'Passkey sign-in was cancelled.' };
      }
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || String(d)).join('; ')
        : detail || error.response?.data?.message || 'Passkey sign-in failed. Please try again.';
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // `birthYear` added 2026-09-18 (KAN-491). The backend has always accepted it — the schema
  // comment reads "the application/signup form is expected to send it" — and nothing ever did,
  // so every account was created with no age. Optional here so callers that do not have one
  // (a learner arriving at /register directly) behave exactly as before.
  const register = async (name, email, password, inviteCode, birthYear = null) => {
    setLoading(true);
    try {
      const response = await authAPI.register({
        name,
        email,
        password,
        invite_code: inviteCode,
        ...(birthYear ? { birth_year: birthYear } : {}),
      });
      const { access_token: newToken, user: userData } = response.data;

      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || String(d)).join('; ')
        : detail || error.response?.data?.message || 'Registration failed. Please try again.';
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Logging out must end the session on the server, not only forget the token here.
  // Until 3 Oct 2026 this cleared sessionStorage and nothing else: the server session
  // stayed live and the httponly access and refresh cookies set at login stayed in the
  // browser, so the next request on this device could still be signed in (KAN-443 found
  // POST /auth/logout had no caller). The local state is cleared whatever the server says,
  // so a network error never leaves someone looking signed in.
  // Pass { revoke: false } when the session is already gone (after logout-all or
  // account deletion), so we don't call a route that can only answer 401.
  // The server is called even when this tab holds no token: the httponly cookies may
  // still authenticate the call, and the server revokes the session from the refresh
  // cookie when the access token has expired (KAN-531 follow-up).
  const logout = async ({ revoke = true } = {}) => {
    try {
      if (revoke) {
        await authAPI.logout();
      }
    } catch (e) {
      // Best effort: the local sign-out below still happens.
    } finally {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      setToken(null);
      setUser(null);
    }
  };

  // EditProfilePage has destructured `updateUser` from this context since the repository's
  // first commit, and the context has never provided it. Saving a profile therefore always
  // threw "updateUser is not a function" — minified in production to "r is not a function".
  //
  // The throw happens AFTER users.updateProfile() resolves, so the save itself succeeded
  // every time; only the client-side refresh failed. The page then showed an error toast
  // and stayed put, so the user was told their change was lost when it had actually been
  // written. That is the worst shape this bug could take: it trains people to distrust a
  // save that worked, or to retry it.
  //
  // Refreshing the cached user matters beyond silencing the error — the header and any
  // other consumer read from here, so without it a renamed user keeps the old name until
  // they log out. Tolerates a missing payload rather than clobbering the session with
  // undefined.
  const updateUser = (userData) => {
    if (!userData) return user;
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
    return userData;
  };

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.role === 'admin';
  // editor drafts content in the Content desk; admin does everything an editor can plus approve/manage.
  const isEditor = user?.role === 'editor' || isAdmin;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAuthenticated,
        isAdmin,
        isEditor,
        login,
        loginWithPasskey,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
